import { useQueryClient } from '@tanstack/react-query';
import { queryUtils, useBulkRefreshPdpsMutation, useRefreshPdpMutation } from '@vizit/api';
import { AlertLevel, useToast } from '@vizit/atoms';
import { RefreshPDPDialog, type RefreshPDPDialogProps, RefreshPDPDialogVariant } from '@vizit/organisms';
import * as React from 'react';
import { useRetailerRegion } from '../../hooks/use-retailer-region';

interface RefreshProductDialogProps extends Omit<RefreshPDPDialogProps, 'onSubmit'> {
  /**
   * The portfolio ID for the product(s) to refresh
   */
  portfolioId: string;
  /**
   * The product ID to refresh (required when variant is Single)
   */
  productId?: string;
  /**
   * Callback when the refresh is successfully submitted
   */
  onRefreshSubmitted?: () => void;
  /**
   * Callback to notify when refresh starts (for showing indicators)
   */
  onRefreshStarted?: () => void;
}

/**
 * Wrapper component for RefreshPDPDialog that handles API calls and toast notifications
 * Supports both single product refresh and bulk refresh
 *
 * @param props - The component props
 * @param props.portfolioId - Portfolio ID for the product(s) to refresh
 * @param props.productId - Product ID to refresh (required when variant is Single)
 * @param props.variant - The variant of the dialog (single or all products)
 * @param props.onRefreshSubmitted - Callback when the refresh is successfully submitted
 * @param props.isOpen - Whether the dialog is open
 * @param props.onOpenChange - Callback when dialog open state changes
 * @param props.triggerButtonContent - Content for the trigger button
 *
 * @example
 * // Single product refresh
 * <RefreshProductDialog
 *   variant={RefreshPDPDialogVariant.Single}
 *   portfolioId="portfolio-123"
 *   productId="product-456"
 *   isOpen={isOpen}
 *   onOpenChange={setIsOpen}
 * />
 */
function RefreshProductDialog(props: RefreshProductDialogProps) {
  const {
    portfolioId,
    productId,
    variant = RefreshPDPDialogVariant.Single,
    onRefreshSubmitted,
    onRefreshStarted,
    onOpenChange,
    ...other
  } = props;

  const { addToast } = useToast();
  const { data: retailerRegionData } = useRetailerRegion();
  const refreshPdpMutation = useRefreshPdpMutation();
  const bulkRefreshPdpsMutation = useBulkRefreshPdpsMutation();

  const queryClient = useQueryClient();

  const handleSubmit = React.useCallback(async () => {
    try {
      if (variant === RefreshPDPDialogVariant.Single) {
        if (!productId) {
          throw new Error('productId is required for single product refresh');
        }
        await refreshPdpMutation.mutateAsync({
          portfolio_id: portfolioId,
          pdp_id: productId,
        });

        // clear the query cache for the product
        queryUtils.invalidateAllPdpLists(queryClient);
        queryUtils.invalidateProductById(queryClient, productId);

        addToast({
          title: 'Refreshing product now!',
          description: 'This will take a few moments.',
          level: AlertLevel.Info,
        });
      } else {
        await bulkRefreshPdpsMutation.mutateAsync({
          portfolio_id: portfolioId,
          pdps: [],
          refresh_all: true,
        });

        // Notify that refresh has started
        onRefreshStarted?.();

        // Invalidate all products & lists
        queryUtils.invalidateAllPdpLists(queryClient);
        queryUtils.invalidateAllProducts(queryClient);

        addToast({
          title: 'Refreshing all product pages!',
          description: 'This will take a few moments.',
          level: AlertLevel.Info,
        });
      }

      // Close the dialog after successful submission
      onOpenChange?.(false);

      // Call the callback if provided
      onRefreshSubmitted?.();
    } catch (error) {
      addToast({
        title: 'Failed to refresh',
        description: error instanceof Error ? error.message : 'An unexpected error occurred',
        level: AlertLevel.Error,
      });
    }
  }, [
    variant,
    productId,
    portfolioId,
    refreshPdpMutation,
    bulkRefreshPdpsMutation,
    addToast,
    onOpenChange,
    onRefreshSubmitted,
    onRefreshStarted,
    queryClient,
  ]);

  return (
    <RefreshPDPDialog
      variant={variant}
      onSubmit={handleSubmit}
      onOpenChange={onOpenChange}
      retailerDisplayName={retailerRegionData?.display_name}
      {...other}
    />
  );
}

export { RefreshProductDialog };
export type { RefreshProductDialogProps };
